import { NavbarSection } from "@src/gql/graphql";
import { tool } from "ai";
import { z } from "zod";
import type { LibraryAgentActions } from "./hooks/library-agent-context";

const navbarSectionSchema = z.nativeEnum(NavbarSection);

export function createLibraryTools({
	actions,
}: {
	actions: LibraryAgentActions;
}) {
	return {
		listNavbarTree: tool({
			description:
				"List the My Workspace navbar tree (boards and folders) with parent keys.",
			inputSchema: z.object({
				section: navbarSectionSchema.optional(),
			}),
			execute: async ({ section }) => actions.listNavbarTree({ section }),
		}),
		createWhiteboard: tool({
			description:
				"Create a new whiteboard in My Workspace, optionally inside a folder.",
			inputSchema: z.object({
				name: z.string().min(1),
				parentFolderKey: z.string().nullable().optional(),
			}),
			execute: async ({ name, parentFolderKey }) =>
				actions.createWhiteboard({ name, parentFolderKey }),
		}),
		renameWhiteboard: tool({
			description: "Rename a whiteboard in My Workspace.",
			inputSchema: z.object({
				whiteboardId: z.string().min(1),
				name: z.string().min(1),
			}),
			execute: async ({ whiteboardId, name }) =>
				actions.renameWhiteboard({ whiteboardId, name }),
		}),
		createFolder: tool({
			description: "Create a folder in My Workspace.",
			inputSchema: z.object({
				name: z.string().min(1),
				parentFolderKey: z.string().nullable().optional(),
			}),
			execute: async ({ name, parentFolderKey }) =>
				actions.createFolder({ name, parentFolderKey }),
		}),
		renameFolder: tool({
			description: "Rename a folder in My Workspace.",
			inputSchema: z.object({
				folderKey: z.string().min(1),
				name: z.string().min(1),
			}),
			execute: async ({ folderKey, name }) =>
				actions.renameFolder({ folderKey, name }),
		}),
		deleteFolder: tool({
			description:
				"Delete an empty folder from My Workspace. Fails if the folder has children.",
			inputSchema: z.object({
				folderKey: z.string().min(1),
			}),
			execute: async ({ folderKey }) => actions.deleteFolder({ folderKey }),
		}),
		moveNavbarItem: tool({
			description:
				"Move a board or folder within My Workspace using parent and neighbor keys.",
			inputSchema: z.object({
				itemKey: z.string().min(1),
				parentKey: z.string().nullable().optional(),
				sourceParentKey: z.string().nullable().optional(),
				beforeKey: z.string().nullable().optional(),
				afterKey: z.string().nullable().optional(),
			}),
			execute: async (input) => actions.moveNavbarItem(input),
		}),
		duplicateWhiteboard: tool({
			description: "Duplicate a whiteboard in My Workspace.",
			inputSchema: z.object({
				whiteboardId: z.string().min(1),
				name: z.string().optional(),
			}),
			execute: async ({ whiteboardId, name }) =>
				actions.duplicateWhiteboard({ whiteboardId, name }),
		}),
		deleteWhiteboard: tool({
			description:
				"Delete a whiteboard to trash (same as navbar Delete). " +
				"Blocked for shared or published boards. Not a permanent delete.",
			inputSchema: z.object({
				whiteboardId: z.string().min(1),
			}),
			execute: async ({ whiteboardId }) =>
				actions.deleteWhiteboard({ whiteboardId }),
		}),
	};
}
